// components/home.jsx — Corporate-engineering homepage for Enginuity
// Hatch / DRA / EPCM aesthetic — full-bleed photo hero, image-led service cards,
// markets grid, difference, projects, insights, contact band.

'use client';

import React, { useState as useState_h, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { Section, Eyebrow, Headline, Photo, Button, ReadMore } from './ui';
import { useOnNav } from '@/lib/nav';
import { CAPABILITIES, MARKETS, CLIENTS, PROJECTS } from '@/lib/site-data';

// Code-split out of the homepage's main JS chunk — this is ~180KB of
// isometric SVG explorer, and was previously bundled and hydrated eagerly
// with the rest of the page. ssr:true keeps its content in the prerendered
// HTML (so crawlers still see the discipline copy), but the chunk itself
// now loads and evaluates independently, well clear of the initial
// hydration path that Total Blocking Time measures.
//
// The explorer is a ~2,500-node SVG tree, so hydrating it costs several
// seconds of main-thread time on a throttled mobile CPU (confirmed by
// diffing a Lighthouse run with/without it — it accounts for the large
// majority of the homepage's main-thread work). It's rendered inside
// <Suspense> below so React 18's selective hydration can hydrate it as
// its own boundary: the header/nav/rest of the page hydrate first and
// stay interactive, and the plant's hydration is scheduled around that
// instead of blocking everything in one synchronous pass. The SSR markup
// (and all of its visual content) is unchanged — this only changes when
// React attaches interactivity to it, not what's painted.
const PlantExplorer = dynamic(() => import('./plant').then((m) => m.PlantExplorer), {
  ssr: true,
  loading: () => <div style={{ minHeight: 640, background: '#0f1114' }} />,
});
const PLANT_FALLBACK = <div style={{ minHeight: 640, background: '#0f1114' }} />;

// ── Welcome — DRA-style intro paragraph ────────────────────────────────────
function Welcome({ onNav }) {
  return (
    <Section id="welcome">
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 0.6fr) minmax(0, 1fr)',
        gap: 56, alignItems: 'start',
      }} className="enq-2col">
        <div>
          <Eyebrow>Welcome</Eyebrow>
          <Headline size="lg" as="h2" style={{ marginTop: 20, maxWidth: '15ch' }}>
            A different kind of engineering firm.
          </Headline>
        </div>
        <div>
          <p style={{
            fontSize: 'clamp(18px, 1.4vw, 22px)', lineHeight: 1.55,
            color: 'var(--fg2)', margin: 0, maxWidth: '58ch',
          }}>
            Enginuity is a multi-disciplinary engineering, project delivery and operations
            partner, focused on the mining, minerals and industrial sectors. We bring together
            advanced research and turn-key EPCM delivery in a single team, so projects move from
            concept to commissioned asset without losing the engineers who started them.
          </p>
          <p style={{
            marginTop: 22, fontSize: 16, lineHeight: 1.65,
            color: 'var(--fg2)', margin: '22px 0 0', maxWidth: '58ch',
          }}>
            From our headquarters in Centurion, South Africa and our research office in
            St Gallen, Switzerland, we serve Anglo American, Black Chrome Mine, Aurex
            Constructors, General Kinematics, Iritron and R&amp;R Quantity Surveyors, among others.
          </p>
          <div style={{ marginTop: 32 }}>
            <ReadMore onClick={() => onNav('about')}>About Enginuity</ReadMore>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ServiceCard({ cap, onNav }) {
  const [h, setH] = useState_h(false);
  return (
    <article onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
             onClick={() => onNav(`capabilities/${cap.id}`)}
             style={{
      background: 'var(--bg)', cursor: 'pointer',
      border: '1px solid var(--hair)',
      transition: 'box-shadow .25s, transform .25s',
      boxShadow: h ? '0 12px 32px rgba(0,0,0,0.08)' : '0 1px 0 rgba(0,0,0,0.02)',
      transform: h ? 'translateY(-2px)' : 'translateY(0)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          transform: h ? 'scale(1.03)' : 'scale(1)',
          transition: 'transform .8s ease',
        }}>
          <Photo aspect="16/9" tone={cap.tone} caption={`${cap.label}, Enginuity`} />
        </div>
        <div style={{
          position: 'absolute', top: 18, left: 18,
          padding: '6px 10px', background: 'rgba(255,255,255,0.92)',
          fontFamily: '"Source Sans 3", sans-serif', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)',
          whiteSpace: 'nowrap',
        }}>{cap.tag}</div>
      </div>
      <div style={{ padding: '28px 30px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Headline size="md" as="h3" style={{ maxWidth: '18ch' }}>{cap.label}</Headline>
        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: 'var(--fg2)', maxWidth: '46ch' }}>
          {cap.blurb}
        </p>
        <div style={{ marginTop: 8 }}>
          <ReadMore color="var(--fg)" onClick={(e) => { e?.stopPropagation && e.stopPropagation(); onNav(`capabilities/${cap.id}`); }}>
            Read more
          </ReadMore>
        </div>
      </div>
    </article>
  );
}

function MarketTile({ market, span, onNav }) {
  const [h, setH] = useState_h(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            onClick={() => onNav('markets')} style={{
      gridColumn: `span ${span}`,
      appearance: 'none', border: 0, padding: 0,
      position: 'relative', overflow: 'hidden',
      cursor: 'pointer', textAlign: 'left',
      background: '#0f1114',
    }} className="enq-market-tile">
      <div style={{
        transform: h ? 'scale(1.04)' : 'scale(1)',
        transition: 'transform .9s ease',
      }}>
        <Photo aspect={span >= 3 ? '16/10' : '4/5'} tone={market.tone} caption={market.label} />
      </div>
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(15,17,20,${h ? 0.85 : 0.7}) 0%, rgba(15,17,20,${h ? 0.3 : 0.15}) 60%, transparent 100%)`,
        transition: 'background .3s',
        padding: 28, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}>
        <Headline size="md" as="h3" style={{ color: '#ffffff', maxWidth: '14ch' }}>{market.label}</Headline>
        <div style={{
          maxHeight: h ? 80 : 0, overflow: 'hidden',
          transition: 'max-height .35s ease, opacity .35s',
          opacity: h ? 1 : 0,
        }}>
          <p style={{
            margin: '12px 0 0', fontSize: 14, lineHeight: 1.5,
            color: 'rgba(255,255,255,0.85)', maxWidth: '38ch',
          }}>{market.blurb}</p>
        </div>
      </div>
    </button>
  );
}

// ── Offices — two offices, one team ───────────────────────────────────────
function Difference({ onNav }) {
  return (
    <Section id="difference" style={{ background: 'var(--bg2)' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 32, alignItems: 'end', marginBottom: 48,
      }} className="enq-2col">
        <div>
          <Eyebrow>Offices</Eyebrow>
          <Headline size="lg" as="h2" style={{ marginTop: 20, maxWidth: '14ch' }}>
            Two offices, one team.
          </Headline>
        </div>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--fg2)', margin: 0, maxWidth: '46ch' }}>
          Headquartered in South Africa with a research arm in Switzerland, 
          so the R&amp;D bench and the EPCM delivery floor stay connected.
        </p>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 28,
      }} className="enq-2col">
        {[
          { tag: 'South Africa', city: 'Centurion', role: 'Engineering & EPCM headquarters', detail: ['Limeroc Business Park', 'Mini Factory Unit 27, Magner Street', 'Knoppieslaagte 385-JR, Centurion 0157'], img: '/assets/office-centurion.webp', imgSm: '/assets/office-centurion-sm.webp', pos: 'center', tone: 'earth' },
          { tag: 'Switzerland',  city: 'St Gallen', role: 'Research & technology development',  detail: ['Lerchenfeldstrasse 3', '9014 St Gallen', 'Switzerland'], img: '/assets/office-stgallen.webp', imgSm: '/assets/office-stgallen-sm.webp', pos: 'center 40%', tone: 'sky' },
        ].map((o, i) => (
          <article key={i} style={{ background: 'var(--bg)', border: '1px solid var(--hair)' }}>
            <Photo aspect="16/9" src={o.img} srcSm={o.imgSm} objectPosition={o.pos} tone={o.tone} caption={`${o.city} office, Enginuity`} />
            <div style={{ padding: '28px 32px 32px' }}>
              <span style={{
                fontFamily: '"Source Sans 3", sans-serif', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)',
              }}>{o.tag}</span>
              <Headline size="md" as="h3" style={{ marginTop: 12 }}>{o.city}</Headline>
              <p style={{ margin: '8px 0 24px', fontSize: 15, color: 'var(--fg2)' }}>{o.role}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {o.detail.map((line, j) => (
                  <li key={j} style={{ fontSize: 15, color: 'var(--fg2)', lineHeight: 1.5 }}>{line}</li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

// ── Stats band ─────────────────────────────────────────────────────────────
function Stats() {
  return (
    <section style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}>
      <div style={{
        maxWidth: 1320, margin: '0 auto', padding: '64px 32px',
        display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 32,
      }} className="enq-stats">
        {[
          ['09', 'Years operating'],
          ['24', 'Projects delivered'],
          ['> R 1.4 bn', 'Combined project value'],
          ['02', 'Continents · ZA + CH'],
        ].map(([n, l], i) => (
          <div key={i}>
            <div style={{
              fontFamily: 'var(--ff-display)', fontWeight: 'var(--display-w)',
              letterSpacing: 'var(--display-track)',
              fontSize: 'clamp(42px, 5vw, 64px)', lineHeight: 1,
            }}>{n}</div>
            <div style={{
              marginTop: 12, fontSize: 14, fontWeight: 500,
              letterSpacing: '0.04em', opacity: 0.9,
            }}>{l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Featured projects ──────────────────────────────────────────────────────
function FeaturedWork({ onNav }) {
  return (
    <Section id="projects">
      <div style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 32, alignItems: 'end', marginBottom: 48,
      }} className="enq-2col">
        <div>
          <Eyebrow>Selected projects</Eyebrow>
          <Headline size="lg" as="h2" style={{ marginTop: 20, maxWidth: '14ch' }}>
            Built, commissioned, running.
          </Headline>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 14, color: 'var(--mid)' }}>04 of 24 featured</span>
          <Button onClick={() => onNav('work')} variant="outline" size="md">All projects</Button>
        </div>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 32,
      }} className="enq-projects-grid">
        {PROJECTS.map((p) => (
          <ProjectCard key={p.id} project={p} onNav={onNav} />
        ))}
      </div>
    </Section>
  );
}

function ProjectCard({ project, onNav }) {
  const [h, setH] = useState_h(false);
  return (
    <article onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
             onClick={() => onNav('work')} style={{ cursor: 'pointer' }}>
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ transform: h ? 'scale(1.03)' : 'scale(1)', transition: 'transform .8s ease' }}>
          <Photo aspect="4/3" tone={project.tone} caption={project.title} />
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <span style={{
            fontFamily: '"Source Sans 3", sans-serif', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)',
          }}>{project.sector}</span>
          <span style={{ fontSize: 13, color: 'var(--mid)' }}>{project.year}</span>
        </div>
        <Headline size="md" as="h3" style={{ maxWidth: '20ch' }}>{project.title}</Headline>
        <p style={{ margin: '14px 0 16px', fontSize: 15, lineHeight: 1.55, color: 'var(--fg2)', maxWidth: '54ch' }}>
          {project.blurb}
        </p>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          paddingTop: 14, borderTop: '1px solid var(--hair)',
        }}>
          <span style={{
            fontSize: 14, fontWeight: 700, color: 'var(--accent)',
          }}>{project.metric}</span>
          <span style={{ color: 'var(--ash)' }}>·</span>
          <span style={{ fontSize: 13, color: 'var(--mid)' }}>{project.location}</span>
        </div>
      </div>
    </article>
  );
}

// ── Clients ────────────────────────────────────────────────────────────────
function Clients() {
  return (
    <Section id="clients" style={{ background: 'var(--bg2)' }}>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <Eyebrow style={{ display: 'inline-flex' }}>Trusted by</Eyebrow>
        <Headline size="md" as="h2" style={{ marginTop: 16 }}>
          Working with the operators who keep industry moving.
        </Headline>
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0,
        border: '1px solid var(--hair)',
        background: 'var(--bg)',
      }} className="enq-clients-grid">
        {CLIENTS.map((c, i) => (
          <ClientLogo key={c.slug} client={c}
            borderRight={(i + 1) % 3 === 0 ? 'none' : '1px solid var(--hair)'}
            borderBottom={i < CLIENTS.length - (CLIENTS.length % 3 || 3) ? '1px solid var(--hair)' : 'none'} />
        ))}
      </div>
    </Section>
  );
}

// Renders a client's logo image, falling back to a clean wordmark if the
// image file hasn't been uploaded yet (uploads/logos/<slug>.png|svg).
function ClientLogo({ client, borderRight, borderBottom }) {
  const [mode, setMode] = useState_h('png'); // png → svg → wordmark
  const src = mode === 'png'
    ? `/assets/logos/${client.slug}.png`
    : mode === 'svg'
      ? `/assets/logos/${client.slug}.svg`
      : null;
  return (
    <div style={{
      padding: '36px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRight, borderBottom,
      minHeight: 110, textAlign: 'center', color: 'var(--fg2)',
      transition: 'color .2s, background .2s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--fg)'; e.currentTarget.style.background = 'var(--bg2)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--fg2)'; e.currentTarget.style.background = 'transparent'; }}
    >
      {src ? (
        <img src={src} alt={client.name} title={client.name}
             width={client.w} height={client.h} loading="lazy" decoding="async"
             onError={() => setMode(mode === 'png' ? 'svg' : 'wordmark')}
             style={{
               maxHeight: 52, maxWidth: '80%', width: 'auto', height: 'auto', objectFit: 'contain',
               opacity: 0.92, transition: 'opacity .2s, transform .2s',
             }}
             onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.03)'; }}
             onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.92'; e.currentTarget.style.transform = 'scale(1)'; }}
        />
      ) : (
        <span style={{
          fontFamily: 'var(--ff-display)', fontWeight: 600,
          fontSize: 'clamp(15px, 1.2vw, 18px)', letterSpacing: '0.005em',
        }}>{client.name}</span>
      )}
    </div>
  );
}

// ── Contact CTA ────────────────────────────────────────────────────────────
function ContactCTA({ onNav }) {
  return (
    <section style={{ position: 'relative', background: '#0f1114', color: '#ffffff', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <Photo aspect="auto" tone="industrial" parallax={-20} style={{ height: '100%' }} caption="Site supervision, North West, 2023" />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,17,20,0.92) 0%, rgba(15,17,20,0.7) 60%, rgba(15,17,20,0.5) 100%)' }} />
      </div>
      <div style={{
        position: 'relative', maxWidth: 1320, margin: '0 auto', padding: '110px 32px',
        display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: 64, alignItems: 'end',
      }} className="enq-2col">
        <div>
          <Eyebrow color="#ffffff" style={{ opacity: 0.9 }}>Need a solution?</Eyebrow>
          <Headline size="xl" as="h2" style={{ color: '#ffffff', marginTop: 24, maxWidth: '16ch' }}>
            Let's build it together.
          </Headline>
        </div>
        <div>
          <p style={{ fontSize: 'clamp(16px, 1.2vw, 19px)', lineHeight: 1.55, color: 'rgba(255,255,255,0.85)', margin: 0, maxWidth: '42ch' }}>
            Send us a brief (even a rough sketch) and we will come back with how we would approach it,
            who would lead it, and what a first phase might look like.
          </p>
          <div style={{ marginTop: 30, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Button onClick={() => onNav('contact')} variant="light" size="lg">Start a conversation</Button>
            <a href="mailto:karla.naude@engn.co.za" style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: '15px 26px', textDecoration: 'none',
              border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)',
              color: '#ffffff', fontFamily: '"Source Sans 3", sans-serif', fontSize: 16, fontWeight: 600,
              backdropFilter: 'blur(8px)',
            }}>karla.naude@engn.co.za</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Home compose ───────────────────────────────────────────────────────────
function HomePage() {
  const onNav = useOnNav();
  return (
    <>
      <Suspense fallback={PLANT_FALLBACK}>
        <PlantExplorer embedded={true} />
      </Suspense>
      <Welcome onNav={onNav} />
      <Difference onNav={onNav} />
      <Stats />
      <Clients />
      <ContactCTA onNav={onNav} />
    </>
  );
}

export {
  HomePage,
  Welcome, ServiceCard, MarketTile,
  Difference, Stats, FeaturedWork, ProjectCard, Clients, ContactCTA,
};
